import { useState, useMemo, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Chip, Icon, Toast } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { useQuery, useMutation } from "@/hooks/useApi";
import { usePublications } from "@/hooks/usePublications";
import { useNewAxisEdition } from "@/hooks/useNewAxisEditions";
import { useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { taxonomyValueFitsMode } from "@/utils/taxonomyFilter";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import {
  mailingGroupsService,
  type MailingGroup,
  type MailingGroupDetailRegion,
  type CreateMailingGroupBody,
  type UpdateMailingGroupBody,
} from "@/api/mailing-groups.service";
import { ROUTES } from "@/routes";
import { formatDateShort } from "@/utils/dateFormatter";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { AddGroupModal } from "./AddGroupModal";
import { campaignStatusChip } from "./campaign-display";
import { isGroupLocked, publicationDisplayLabel } from "./mailingGroups.utils";
import styles from "./MailingGroups.module.css";

type EditPayload = {
  id: string | number;
  name: string;
  regions: MailingGroupDetailRegion[];
};

const DEFAULT_ITEMS_PER_PAGE = 10;

type ApiError = Error & { status?: number; code?: string };

function statusOf(error: unknown): number | undefined {
  return (error as ApiError | undefined)?.status;
}

function codeOf(error: unknown): string | undefined {
  return (error as ApiError | undefined)?.code;
}

/** Small label shown next to bar-group names; standard groups render nothing. */
function categoryLabel(category: MailingGroup["category"]): string | null {
  if (category === "instructing_solicitor") return "Instructing solicitor";
  if (category === "other") return "Other";
  return null;
}

/**
 * Bar groups exist as two linked siblings (Instructing solicitor / Other);
 * deleting one deletes both, so warn accordingly. Standard groups (category
 * null) only remove their own regions.
 */
function deleteWarningMessage(category: MailingGroup["category"]): string {
  if (category === "instructing_solicitor" || category === "other") {
    return "This is a Bar group — both parts (Instructing solicitor / Other) will be deleted. This action cannot be undone.";
  }
  return "This action cannot be undone.";
}

function MailingGroupDetail() {
  const { publicationId = "", edition = "" } = useParams<{
    publicationId: string;
    edition: string;
  }>();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editPayload, setEditPayload] = useState<EditPayload | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<MailingGroup | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const { toggleVisible, ready } = useTaxonomyMode();
  const legacyParam = taxonomyValueFitsMode(publicationId, "legacy");
  const newAxis = toggleVisible && !legacyParam;
  const editionId = newAxis ? publicationId : undefined;

  const { data: publicationsData } = usePublications(
    { edition: edition || undefined },
    { enabled: legacyParam || (ready && !newAxis) },
  );

  const { data: newAxisEdition } = useNewAxisEdition(editionId);

  const publication = useMemo(() => {
    const pubs = publicationsData?.publications ?? [];
    return pubs.find((p: LegacyPublication) => String(p.id) === publicationId) ?? null;
  }, [publicationsData?.publications, publicationId]);

  const publicationName = newAxis
    ? newAxisEdition?.publication || `Publication ${publicationId}`
    : publication?.publication || `Publication ${publicationId}`;
  const publicationEdition = newAxis
    ? newAxisEdition?.editionName || undefined
    : publication?.edition;
  const publicationLabel = publicationDisplayLabel(publicationName, publicationEdition);

  const offset = (currentPage - 1) * itemsPerPage;

  const {
    data: listResponse,
    loading,
    error,
    refetch,
    isRefetching,
  } = useQuery(
    () =>
      ready
        ? mailingGroupsService.list({
            ...(editionId !== undefined ? { editionId } : { publicationId: Number(publicationId) }),
            edition,
            taxonomy: toggleVisible ? (newAxis ? "new" : "legacy") : undefined,
            limit: itemsPerPage,
            offset,
          })
        : Promise.resolve(null),
    [ready, toggleVisible, editionId, publicationId, edition, currentPage, itemsPerPage],
  );

  const { mutate: createGroup } = useMutation((body: CreateMailingGroupBody) =>
    mailingGroupsService.create(body),
  );

  const groups = listResponse?.groups ?? [];
  const totalCount = listResponse?.pagination.total ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleOpenGroup = useCallback(
    (group: MailingGroup) => {
      navigate(ROUTES.surveys.campaignGroupDetail(publicationId, edition, String(group.id)));
    },
    [navigate, publicationId, edition],
  );

  const handleAddGroup = useCallback(
    async (body: CreateMailingGroupBody) => {
      try {
        await createGroup(body);
        await refetch();
        setToast({ message: "Mailing group added successfully.", type: "success" });
      } catch (err) {
        const status = statusOf(err);
        if (status === 409 && codeOf(err) === "GROUP_NAME_TAKEN") {
          setToast({
            message: "A group with this name already exists for this publication/edition.",
            type: "error",
          });
        } else if (status === 409) {
          // Region already assigned in this publication (Rule 4). Refetch so the
          // region picker reflects the now-used region.
          await refetch();
          setToast({
            message: "This region is already assigned to a group in this publication.",
            type: "error",
          });
        } else if (status === 400) {
          setToast({
            message: "Cannot mix bar and standard regions in one mailing group.",
            type: "error",
          });
        } else {
          const message =
            err instanceof Error ? err.message : "Failed to add mailing group. Please retry.";
          setToast({ message, type: "error" });
        }
        throw err;
      }
    },
    [createGroup, refetch],
  );

  const handleEditGroup = useCallback(async (group: MailingGroup) => {
    try {
      const { group: detail } = await mailingGroupsService.getById(group.id);
      setEditPayload({
        id: group.id,
        name: detail.name,
        regions: detail.regions,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load mailing group. Please retry.";
      setToast({ message, type: "error" });
    }
  }, []);

  const handleUpdateGroup = useCallback(
    async (id: string | number, body: UpdateMailingGroupBody) => {
      try {
        await mailingGroupsService.update(id, body);
        await refetch();
        setToast({ message: "Mailing group updated successfully.", type: "success" });
      } catch (err) {
        const status = statusOf(err);
        if (status === 409 && codeOf(err) === "GROUP_NAME_TAKEN") {
          setToast({
            message: "A group with this name already exists for this publication/edition.",
            type: "error",
          });
        } else if (status === 409 && codeOf(err) === "MAILING_GROUP_LOCKED") {
          // Raced with a campaign start: refetch so the row locks in the UI.
          await refetch();
          setToast({
            message: "This group's campaign has started and can no longer be edited.",
            type: "error",
          });
        } else if (status === 409) {
          await refetch();
          setToast({
            message: "This region is already assigned to a group in this publication.",
            type: "error",
          });
        } else if (status === 400) {
          setToast({
            message: "Cannot mix bar and standard regions in one mailing group.",
            type: "error",
          });
        } else {
          const message =
            err instanceof Error ? err.message : "Failed to update mailing group. Please retry.";
          setToast({ message, type: "error" });
        }
        throw err;
      }
    },
    [refetch],
  );

  const title = `${edition} - ${publicationLabel}`;

  // Edit/Delete live inside a clickable row, so they must not bubble up to the
  // row's navigation handler.
  const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

  const columns: Column<MailingGroup>[] = [
    {
      header: "Group",
      accessor: "name",
      render: (_value, group) => {
        const groupName = group.name || `Group ${group.id}`;
        const category = categoryLabel(group.category);
        return (
          <span className={styles.nameCell}>
            <span className={styles.nameText}>{groupName}</span>
            {category && (
              <Chip size="small" color="primary">
                {category}
              </Chip>
            )}
          </span>
        );
      },
    },
    {
      header: "Campaign status",
      accessor: "campaign_status",
      render: (_value, group) => {
        // No campaigns yet → genuine "Not started". Otherwise render the real
        // effective status with the SAME chip helper as the group-detail view.
        if (!group.campaign_status) {
          return (
            <Chip size="small" color="default" className={styles.neutralStatusChip}>
              Not started
            </Chip>
          );
        }
        const chip = campaignStatusChip(group.campaign_status);
        return (
          <Chip
            size="small"
            color={chip.color}
            className={chip.color === "default" ? styles.neutralStatusChip : undefined}
          >
            <span className={styles.statusChip}>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: chip.dotColor }}
                aria-hidden="true"
              />
              {chip.label}
            </span>
          </Chip>
        );
      },
    },
    {
      header: "Response rate",
      accessor: "id",
      render: (_value, group) =>
        group.total_referees === 0 ? "—" : `${group.responded}/${group.total_referees}`,
    },
    {
      header: "Campaign start",
      accessor: "id",
      render: (_value, group) =>
        group.campaign_started_at ? formatDateShort(group.campaign_started_at) : "—",
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value, group) => {
        const groupName = group.name || `Group ${group.id}`;
        const locked = isGroupLocked(group);
        const lockedTitle = locked ? "Locked — campaign already started." : undefined;
        return (
          <span className={styles.actionsCell}>
            <Button
              variant="outlined"
              size="small"
              className={styles.actionButton}
              onClick={event => {
                stopRowClick(event);
                void handleEditGroup(group);
              }}
              disabled={locked}
              title={lockedTitle}
              aria-label={`Edit ${groupName}`}
            >
              <Icon icon="edit" size="small" />
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              className={styles.actionDeleteButton}
              onClick={event => {
                stopRowClick(event);
                setDeleteGroup(group);
              }}
              disabled={locked}
              title={lockedTitle}
              aria-label={`Delete ${groupName}`}
            >
              <Icon icon="delete" size="small" />
            </Button>
          </span>
        );
      },
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title={title} subtitle={`${totalCount} groups`}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<Icon icon="chevron-left" size="small" />}
          onClick={() => navigate(ROUTES.surveys.campaigns)}
        >
          Back to Survey mailing
        </Button>
      </PageHeader>

      <p className={styles.breadcrumb}>
        <Link to={ROUTES.surveys.campaigns} className={styles.breadcrumbLink}>
          Survey mailing
        </Link>{" "}
        / {edition} - {publicationLabel}
      </p>

      <div className={styles.recipientsHeaderRow}>
        <h2 className={styles.sectionHeading}>Mailing groups</h2>
        <div className={styles.campaignsActions}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setIsAddModalOpen(true)}
            startIcon={<Icon icon="plus" size="small" />}
          >
            Add group
          </Button>
        </div>
      </div>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {(loading || !ready) && listResponse === null ? (
        <LoadingState message="Loading mailing groups..." />
      ) : !error && groups.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No mailing groups found"
          message="This publication and edition has no mailing groups yet. Add a group to get started."
        />
      ) : (
        !error && (
          <>
            <div className={styles.tableContainer}>
              <DataTable
                data={groups}
                columns={columns}
                rowKey="id"
                onRowClick={handleOpenGroup}
                emptyMessage="No mailing groups found."
              />
            </div>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + groups.length}
              totalCount={totalCount}
              itemName="mailing groups"
              onPageChange={setCurrentPage}
            />
          </>
        )
      )}

      <AddGroupModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        publicationId={editionId !== undefined ? null : Number(publicationId)}
        editionId={editionId}
        publicationName={publicationName}
        publicationEdition={publicationEdition}
        edition={edition}
        onSave={handleAddGroup}
      />

      {editPayload && (
        <AddGroupModal
          isOpen
          onClose={() => setEditPayload(null)}
          publicationId={editionId !== undefined ? null : Number(publicationId)}
          editionId={editionId}
          publicationName={publicationName}
          publicationEdition={publicationEdition}
          edition={edition}
          onSave={handleAddGroup}
          editGroup={editPayload}
          onUpdate={handleUpdateGroup}
        />
      )}

      {deleteGroup && (
        <ConfirmationModal
          title="Delete group"
          message={`Delete group "${deleteGroup.name || `Group ${deleteGroup.id}`}"?`}
          warningMessage={deleteWarningMessage(deleteGroup.category)}
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setDeleteGroup(null)}
          onConfirm={async () => {
            try {
              await mailingGroupsService.remove(deleteGroup.id);
              await refetch();
              setDeleteGroup(null);
              setToast({ message: "Mailing group deleted successfully.", type: "success" });
            } catch (err) {
              if (statusOf(err) === 409 && codeOf(err) === "MAILING_GROUP_LOCKED") {
                // Raced with a campaign start: refetch so the row locks in the UI.
                await refetch();
                setDeleteGroup(null);
                setToast({
                  message: "This group's campaign has started and can no longer be deleted.",
                  type: "error",
                });
              } else {
                setToast({
                  message: err instanceof Error ? err.message : "Failed to delete mailing group.",
                  type: "error",
                });
              }
              throw err;
            }
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default MailingGroupDetail;
